import { useRouter } from 'next/router';
import { GoogleAnalytics } from '@next/third-parties/google';
import { GA_MEASUREMENT_ID } from '../../util/analytics';
import { isTokenBearingRoute } from '../../util/redactUrl';

/**
 * Centered — unauthenticated auth/signup shell (/login, /create-account,
 * /signup/free, /onboarding/free, /magic-login…). See src/layouts/index.js for
 * the route allowlist.
 *
 * GA is mounted here as well as in Empty because the trial-signup routes live
 * under THIS layout, and `trial_started` cannot fire on a page where gtag was
 * never loaded — the marketing funnel would end at the CTA click. Same
 * measurement id as Empty (imported, not re-typed): two ids would split one
 * visitor's landing-page → signup journey across two GA4 properties.
 *
 * Side effect worth knowing: /login is under this layout too, so returning
 * users generate GA4 pageviews they previously didn't. Filter on page_path if
 * that noise reaches acquisition reporting.
 *
 * ☠️ GA is NOT mounted on the token-bearing auth pages (D18): /reset-password
 * and /[slug]/reset-password (?code= — password reset AND teammate invites),
 * /magic-login (?loginToken=), /connect/[provider]/redirect (?access_token=
 * &id_token=), /redirect and /[slug]/redirect (Shippo ?code=) and
 * /email-confirmation. gtag's page_view sends the full location.href, this
 * mount is not consent-gated, and those URLs are live credentials. They are
 * transient pages entered by a full page load from an email or an OAuth
 * provider and fire no GA key event, so the funnel loses nothing.
 */
const Centered = ({ children }) => {
  const router = useRouter();
  const mountGa = !isTokenBearingRoute(router?.pathname);
  return (
    <div
      data-layout='centered'
      className='font-sans w-full h-screen overflow-y-auto flex items-start justify-center bg-gray-50 dark:bg-gray-900'
    >
      {mountGa && <GoogleAnalytics gaId={GA_MEASUREMENT_ID} />}
      {children}
    </div>
  );
};

export default Centered;
